import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { RootState } from '../../../../@types/RootState';
import { queryClient } from '../../../../App';
import { AlertWithTimestamp } from '../../../../components/AlertWithTimestamp';
import ExceptionSaveDatapoint from '../../../../data/ExceptionSaveDatapoint';
import { selectorPrecoAtacadoDatapoints, UPDATE_PRECO_ATACADO_DATAPOINT_BY_ID } from '../../../../reducers/PrecoAtacado/datapoints';
import { putClearWholesaleFields } from '../pages/PrecoAtacado/services';
import { Wholesale } from '../pages/PrecoAtacado/types';

const DATAPOINTS_QUERY_KEY = ['ipa/wholesale/datapoints'];

export const useClearWholesaleFields = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  // A linha é identificada por productsToBePricedId, não por índice: a lista pode
  // ser trocada (paginação, ordenação, refetch) enquanto a requisição está em voo.
  return useCallback(
    async (rowData: Wholesale.RowData) => {
      const { productsToBePricedId, productId, storeId, storeName, triggerCount, discountPercentage } = rowData;

      const findCurrentRow = () => {
        return selectorPrecoAtacadoDatapoints(store.getState()).find(datapoint => datapoint.productsToBePricedId === productsToBePricedId);
      };

      dispatch(
        UPDATE_PRECO_ATACADO_DATAPOINT_BY_ID({
          productsToBePricedId,
          data: { triggerCount: null, discountPercentage: null },
        }),
      );

      try {
        const { data } = await putClearWholesaleFields({ productsToBePricedId, productId, storeId });

        // Corpo vazio chega como string '' no axios, e spread de string vira índices.
        const hasBody = !!data && typeof data === 'object';

        // O backend é a fonte da verdade do que sobra na linha depois do clear:
        // pode devolver os valores herdados da estratégia, não necessariamente null.
        if (hasBody) {
          dispatch(
            UPDATE_PRECO_ATACADO_DATAPOINT_BY_ID({
              productsToBePricedId,
              data,
            }),
          );
        }

        // Sem os campos de auditoria no retorno, a linha continuaria mostrando o
        // editor e a data antigos — e o link "Limpar campos", que depende deles.
        // Nesse caso o servidor é a fonte da verdade: recarrega a lista.
        const hasLastUpdate = hasBody && ('lastEditedBy' in data || 'lastEditedDate' in data);

        if (!hasLastUpdate) queryClient.invalidateQueries(DATAPOINTS_QUERY_KEY);
      } catch {
        // Só reverte se a linha continuar vazia. Se o usuário digitou enquanto a
        // requisição estava em voo, o que ele digitou vale mais que o valor antigo.
        const currentRow = findCurrentRow();
        const isStillCleared = !currentRow?.triggerCount && !currentRow?.discountPercentage;

        if (isStillCleared) {
          dispatch(
            UPDATE_PRECO_ATACADO_DATAPOINT_BY_ID({
              productsToBePricedId,
              data: { triggerCount, discountPercentage },
            }),
          );
        }

        const { message } = new ExceptionSaveDatapoint({ productId, storeId, storeName });

        AlertWithTimestamp({ message });
      }
    },
    [dispatch, store],
  );
};
